import { Table } from 'reactstrap';
import { ISearchPositionResult } from '../../../types/position';
import { fullNameAutoFormat } from '../../../utils/format-fullname';
import PaginationBar from '../../common/pagination-bar';
import { useState } from 'react';
import './search-position-role.scss'
import OmsToolTip from "../../common/oms-tooltip/oms-tooltip";
import { Pageable } from '../../../types/common';
import TableHeaders, { TableHeaderInfo } from '../../common/table-header';
import { SearchPositionRoleResultModel } from './search-position-role-result.model';
import { FaRegCircleXmark } from 'react-icons/fa6';
import useAuth from '../../../hook/useAuth';
import Button from '../../common/button';

interface SearchPositionRoleResultProp extends Pageable<ISearchPositionResult> {
    selected: any | undefined;
    onTitleSelect: (item:ISearchPositionResult) => void;
    onIndividualSelect: (item:ISearchPositionResult) => void;
    inputChanged: boolean;
    isInputError: boolean;
}

const TABLE_HEADERS: TableHeaderInfo<SearchPositionRoleResultModel>[] = [
    { header: "Position/Role", accessor: "normalizedPositionRole", sortable: true },
    { header: "Title", accessor: "title", sortable: true, presorted: 'asc' },
    { header: "Individual Name", accessor: "individualFirstName", sortable: true },
    { header: "Position Phone/Fax", accessor: "normalizedPhoneFaxes", sortable: true, cssClass: "phone-column" },
    { header: "Position Email", accessor: "emails", sortable: true },
];

// Private helper to render phone numbers as text or list for better accessibility
function RenderPhones({ phones }: { phones: string[] }): React.JSX.Element {
    const items = (phones || []).filter(p => p && p.trim().length > 0);
    if (items.length === 0) return (<></>);
    if (items.length === 1) return (<>{items[0]}</>);
    return (
        <ul className="undecorated">
            {items.map((p, i) => (<li key={`phone-${i}`}>{p}</li>))}
        </ul>
    );
}

function SearchPositionRoleResult (props:SearchPositionRoleResultProp) {
    const { data:records,pageSize=10, selected, onTitleSelect, onIndividualSelect, inputChanged ,isInputError} = props;

    const [sortedData, setSortedData] = useState<SearchPositionRoleResultModel[]>([]);
    const [pagedData, setPagedData] = useState<SearchPositionRoleResultModel[]>([]);
    const { isAdmin  } = useAuth();

    return (
        <>
            <Table id="positions_table" striped className="fixed">
                <caption className="sr-only">Search results, sortable</caption>
                <TableHeaders headers={TABLE_HEADERS} sortableData={(records || []).map(r => new SearchPositionRoleResultModel(r))} onSort={setSortedData} />

                <tbody>
                    {sortedData.length>0
                        ? pagedData.map((record, index) => {
                            return <tr key={record.positionId+'-'+index}>
                                <td>{record.normalizedPositionRole}</td>
                                <td>
                                    <Button color="link"
                                        className={`oms-button-link ${selected === searchPositionRoleSelectedTitle(record) ? 'oms-button-link-selected' : '' }`}
                                        id={`${record.positionId}-${index}-title`}
                                        onClick={()=>{onTitleSelect(record)}}>
                                            <OmsToolTip positionId={record.positionId}>
                                                    {record.title}
                                            </OmsToolTip>

                                    </Button>
                                    <span> {isAdmin && (!record.active && <FaRegCircleXmark/>)}</span>
                                </td>
                                <td>
                                    <Button color="link"
                                        className={`oms-button-link ${selected === searchPositionRoleSelectedIndividual(record) ? 'oms-button-link-selected' : '' }`}
                                        id={`${record.individualId}-${index}-individual`}
                                        onClick={()=>{onIndividualSelect(record)}}>
                                        {record.individualId?fullNameAutoFormat(record.individualFirstName,record.individualMiddleName,record.individualLastName):''}
                                    </Button>

                                </td>
                                <td>
                                    <RenderPhones phones={record.normalizedPhoneFaxes ? record.normalizedPhoneFaxes.split('\n') : []} />
                                </td>
                                <td>
                                    {record.emails && record.emails.map((email,i) => (
                                        <div key={email.id+"-"+i}>{email.emailAddressTxt}</div>
                                    ))}
                                </td>
                            </tr>
                        })
                        :<tr><td colSpan={TABLE_HEADERS.length} className="text-center">{inputChanged?(isInputError ? 'No results found.' : 'Search results will appear in the table.'):'No position/role titles were found with the exact search match.'}</td></tr>
                    }
                </tbody>
            </Table>

            <PaginationBar data={sortedData} pageSize={pageSize} onPageChange={setPagedData} />
        </>
    )
}

export function searchPositionRoleSelectedIndividual(record: ISearchPositionResult) : string {
    return `search-position-role-selected-individual-${record.individualId ?? 0}-${record.positionId}`;
}

export function searchPositionRoleSelectedTitle(record: ISearchPositionResult) : string {
    // individualId should never be null in this case, but better to be safe
    return `search-position-role-selected-title-${record.individualId ?? 0}-${record.positionId}`;
}

export default SearchPositionRoleResult
